import { __read } from "tslib";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import uPlot from 'uplot';
import { buildPlotContext, PlotContext } from './context';
import { pluginLog } from './utils';
import { usePlotConfig } from './hooks';
import usePrevious from 'react-use/lib/usePrevious';
/**
 * @internal
 * uPlot abstraction responsible for plot initialisation, setup and refresh
 * Receives a data frame that is x-axis aligned, as of https://github.com/leeoniya/uPlot/tree/master/docs#data-format
 * Exposes contexts for plugins registration and uPlot instance access
 */
export var UPlotChart = function (props) {
    var canvasRef = useRef(null);
    var plotInstance = useRef();
    var _a = __read(useState(false), 2), isPlotReady = _a[0], setIsPlotReady = _a[1];
    var prevProps = usePrevious(props);
    var _b = usePlotConfig(props.width, props.height, props.config), isConfigReady = _b.isConfigReady, currentConfig = _b.currentConfig, registerPlugin = _b.registerPlugin;
    var getPlotInstance = useCallback(function () {
        return plotInstance.current;
    }, []);
    // Effect responsible for uPlot updates/initialization logic. It's performed whenever component's props have changed
    useLayoutEffect(function () {
        var _a;
        // 0. Exit early if the component is not ready to initialize uPlot
        if (!currentConfig.current || !canvasRef.current || props.width === 0 || props.height === 0) {
            return;
        }
        // 0. Exit if the data set length is different than number of series expected to render
        // This may happen when GraphNG has not synced config yet with the aligned frame. Alignment happens before the render
        // in the getDerivedStateFromProps, while the config creation happens in componentDidUpdate, causing one more render
        // of the UPlotChart if the config needs to be updated.
        if (currentConfig.current.series.length !== props.data.length) {
            return;
        }
        // 1. When config is ready and there is no uPlot instance, create new uPlot and return
        if (isConfigReady && !plotInstance.current) {
            plotInstance.current = initializePlot(props.data, currentConfig.current, canvasRef.current);
            setIsPlotReady(true);
            return;
        }
        // 2. When dimensions have changed, update uPlot size and return
        if (currentConfig.current.width !== (prevProps === null || prevProps === void 0 ? void 0 : prevProps.width) || currentConfig.current.height !== (prevProps === null || prevProps === void 0 ? void 0 : prevProps.height)) {
            pluginLog('uPlot core', false, 'updating size');
            plotInstance.current.setSize({
                width: currentConfig.current.width,
                height: (_a = currentConfig.current) === null || _a === void 0 ? void 0 : _a.height,
            });
            return;
        }
        // 3. When config has changed re-initialize plot
        if (isConfigReady && props.config !== prevProps.config) {
            if (plotInstance.current) {
                pluginLog('uPlot core', false, 'destroying instance');
                plotInstance.current.destroy();
            }
            plotInstance.current = initializePlot(props.data, currentConfig.current, canvasRef.current);
            return;
        }
        // 4. Otherwise, assume only data has changed and update uPlot data
        updateData(props.config, props.data, plotInstance.current);
    }, [props, isConfigReady]);
    // When component unmounts, clean the existing uPlot instance
    useEffect(function () { return function () { var _a; return (_a = plotInstance.current) === null || _a === void 0 ? void 0 : _a.destroy(); }; }, []);
    // Memoize plot context
    var plotCtx = useMemo(function () {
        return buildPlotContext(isPlotReady, canvasRef, props.data, registerPlugin, getPlotInstance);
    }, [plotInstance, canvasRef, props.data, registerPlugin, getPlotInstance, isPlotReady]);
    return (React.createElement(PlotContext.Provider, { value: plotCtx },
        React.createElement("div", { style: { position: 'relative' } },
            React.createElement("div", { ref: plotCtx.canvasRef, "data-testid": "uplot-main-div" }),
            props.children)));
};
function initializePlot(data, config, el) {
    pluginLog('UPlotChart: init uPlot', false, 'initialized with', data, config);
    return new uPlot(config, data, el);
}
function updateData(config, data, plotInstance) {
    if (!plotInstance || !data) {
        return;
    }
    pluginLog('uPlot core', false, 'updating plot data(throttled log!)', data);
    plotInstance.setData(data);
}
//# sourceMappingURL=Plot.js.map